import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Phone, KeyRound, LogIn } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const EMPTY = { name: "", phone: "", email: "", password: "" };
const inputCls = "mt-2 h-10 w-full rounded-lg border border-white/10 bg-ink px-3 text-sm outline-none focus:border-saffron";

export const TeamPanel = () => {
  const [team, setTeam] = useState([]);
  const [load, setLoad] = useState({});
  const [f, setF] = useState(EMPTY);
  const [loginFor, setLoginFor] = useState(null);

  const refresh = () => {
    api.get("/admin/team").then((r) => setTeam(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/stats").then((r) => setLoad(Object.fromEntries((r.data?.workload ?? []).map((w) => [w.id, w])))).catch(() => {});
  };
  useEffect(() => {
    refresh();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/team", f);
      setF(EMPTY);
      refresh();
      toast.success("Team member added");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveLogin = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/team/${loginFor.id}/login`, { email: loginFor.email, password: loginFor.password });
      setLoginFor(null);
      refresh();
      toast.success("Login saved");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const removeLogin = async (m) => {
    if (!window.confirm(`Turn off login for ${m.name}?`)) return;
    await api.delete(`/admin/team/${m.id}/login`).catch(() => {});
    refresh();
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Their leads will become unassigned.`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      refresh();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-team">
      <form onSubmit={add} className="bento grid gap-4 sm:grid-cols-2 xl:grid-cols-5 xl:items-end" data-testid="team-add-form">
        <label><span className="text-xs uppercase tracking-wider text-slate-400">Name</span>
          <input required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="team-name-input" />
        </label>
        <label><span className="text-xs uppercase tracking-wider text-slate-400">Phone (optional)</span>
          <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="team-phone-input" />
        </label>
        <label><span className="text-xs uppercase tracking-wider text-slate-400">Login email (optional)</span>
          <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="team-email-input" />
        </label>
        <label><span className="text-xs uppercase tracking-wider text-slate-400">Password (min 8)</span>
          <input type="password" minLength={8} required={!!f.email} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="team-password-input" />
        </label>
        <button type="submit" className="btn-primary justify-center" data-testid="team-add-btn"><UserPlus size={16} /> Add member</button>
      </form>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {team.map((m) => (
          <div key={m.id} className="bento" data-testid={`team-member-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-lg font-semibold">{m.name}</p>
                {m.phone && <a href={`tel:${m.phone}`} className="mt-1 flex items-center gap-1.5 text-xs text-slate-400 hover:text-white"><Phone size={12} />{m.phone}</a>}
              </div>
              <button onClick={() => remove(m)} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`team-delete-${m.id}`} aria-label="Remove"><Trash2 size={16} /></button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-ink p-3"><p className="font-display text-2xl font-bold text-amber-300">{load[m.id]?.open ?? 0}</p><p className="text-xs text-slate-400">Open leads</p></div>
              <div className="rounded-xl bg-ink p-3"><p className="font-display text-2xl font-bold text-orange-300">{load[m.id]?.onboarded ?? 0}</p><p className="text-xs text-slate-400">Onboarded</p></div>
            </div>
            <div className="mt-4 border-t border-white/10 pt-4" data-testid={`team-login-${m.id}`}>
              {m.login_email ? (
                <p className="flex items-center gap-2 text-xs text-emerald-300"><LogIn size={13} /> Can log in as {m.login_email}</p>
              ) : (
                <p className="text-xs text-slate-400">No login — admin assigns leads only</p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <button className="btn-ghost px-3 py-1.5 text-xs" onClick={() => setLoginFor({ id: m.id, name: m.name, email: m.login_email || "", password: "" })} data-testid={`team-set-login-${m.id}`}>
                  <KeyRound size={13} /> {m.login_email ? "Change email / password" : "Give login"}
                </button>
                {m.login_email && <button className="btn-ghost px-3 py-1.5 text-xs text-red-300" onClick={() => removeLogin(m)} data-testid={`team-remove-login-${m.id}`}>Turn off login</button>}
              </div>
            </div>
          </div>
        ))}
        {!team.length && <p className="text-sm text-slate-400" data-testid="team-empty">No team members yet. Add people who call and onboard partners.</p>}
      </div>
      <Dialog open={!!loginFor} onOpenChange={(o) => !o && setLoginFor(null)}>
        <DialogContent className="border-white/10 bg-card text-white">
          <DialogHeader><DialogTitle>Login for {loginFor?.name}</DialogTitle></DialogHeader>
          {loginFor && (
            <form onSubmit={saveLogin} className="space-y-4" data-testid="team-login-form">
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">Email</span>
                <input type="email" required value={loginFor.email} onChange={(e) => setLoginFor({ ...loginFor, email: e.target.value })} className={inputCls} data-testid="team-login-email-input" />
              </label>
              <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400">New password (min 8)</span>
                <input type="password" required minLength={8} value={loginFor.password} onChange={(e) => setLoginFor({ ...loginFor, password: e.target.value })} className={inputCls} data-testid="team-login-password-input" />
              </label>
              <p className="text-xs text-slate-400">They sign in at /admin/login and only see leads assigned to them.</p>
              <button type="submit" className="btn-primary w-full justify-center" data-testid="team-login-save-btn">Save login</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
